import { Link } from "react-router-dom";
import { useEffect } from "react";

const LAST_UPDATED = "June 2025";
const COMPANY = "OneCheckUp";
const WEBSITE = "https://1checkup.com";
const CONTACT_EMAIL = "onecheckup@gmail.com";

export default function TermsPage() {
  useEffect(() => { document.title = `Terms of Service | ${COMPANY}`; }, []);

  return (
    <div style={{ minHeight: "100vh", background: "hsl(var(--background))", fontFamily: "inherit" }}>
      {/* Header */}
      <header style={{ borderBottom: "1px solid hsl(var(--border))", padding: "1rem 2rem", display: "flex", alignItems: "center", justifyContent: "space-between", background: "hsl(var(--card))" }}>
        <span style={{ fontWeight: 700, fontSize: "1rem", color: "hsl(var(--foreground))" }}>{COMPANY}</span>
        <Link to="/login" style={{ fontSize: "0.8125rem", color: "hsl(var(--primary))", textDecoration: "none" }}>← Back to login</Link>
      </header>

      <main style={{ maxWidth: "780px", margin: "0 auto", padding: "3rem 2rem 5rem" }}>
        {/* Title */}
        <div style={{ marginBottom: "2.5rem", paddingBottom: "1.5rem", borderBottom: "1px solid hsl(var(--border))" }}>
          <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", marginBottom: "0.5rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>Legal</p>
          <h1 style={{ fontSize: "1.875rem", fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: "0.5rem" }}>Terms of Service</h1>
          <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>Last updated: {LAST_UPDATED} · Effective for all {COMPANY} users</p>
        </div>

        <div style={{ fontSize: "0.9rem", lineHeight: "1.8", color: "hsl(var(--foreground))" }}>

          {/* Intro */}
          <p style={{ marginBottom: "2rem", padding: "1rem 1.25rem", background: "hsl(var(--muted))", borderRadius: "8px", fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>
            Please read these Terms of Service carefully before using {COMPANY}. By registering an account or using our platform, you agree to be bound by these terms. If you do not agree, do not use the service.
          </p>

          <Section title="1. Acceptance of Terms">
            <p>These Terms of Service ("Terms") constitute a legally binding agreement between you ("Clinic", "User", or "you") and {COMPANY} ("we", "us", or "our") governing your access to and use of the {COMPANY} clinic management platform, including all related software, services, and features (collectively, the "Service").</p>
            <p>By accessing or using the Service, you confirm that you are authorised to enter into this agreement on behalf of your clinic or organisation, and that the clinic agrees to be bound by these Terms.</p>
          </Section>

          <Section title="2. Description of Service">
            <p>{COMPANY} is a cloud-based clinic management platform designed for healthcare providers in India. The Service includes:</p>
            <ul>
              <li>Patient registration and record management</li>
              <li>Appointment scheduling and queue management</li>
              <li>Prescription creation and template management</li>
              <li>Invoicing and billing</li>
              <li>In-patient (IPD) management</li>
              <li>Reports and analytics</li>
            </ul>
            <p>We reserve the right to modify, suspend, or discontinue any part of the Service at any time with reasonable notice.</p>
          </Section>

          <Section title="3. Account Registration and Security">
            <p>To use the Service, the clinic administrator must create an account with accurate, complete, and current information. You are responsible for:</p>
            <ul>
              <li>Maintaining the confidentiality of your login credentials</li>
              <li>All activity that occurs under your account</li>
              <li>Notifying us immediately at <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "hsl(var(--primary))" }}>{CONTACT_EMAIL}</a> of any unauthorised access</li>
              <li>Ensuring all staff accounts are created only for authorised personnel</li>
            </ul>
            <p>We reserve the right to suspend accounts where we have reason to believe security has been compromised.</p>
          </Section>

          <Section title="4. Clinic and User Responsibilities">
            <p>As a clinic using this platform, you are responsible for:</p>
            <ul>
              <li>Ensuring that all clinical staff using the platform are authorised and trained</li>
              <li>Verifying the accuracy of all prescriptions, diagnoses, and clinical records entered into the system</li>
              <li>Complying with all applicable laws, including the Indian Medical Council Act, Drugs and Cosmetics Act, and any applicable state medical regulations</li>
              <li>Obtaining patient consent before recording their personal and health data in the system</li>
              <li>Maintaining appropriate physical and organisational security at your premises</li>
            </ul>
            <p style={{ fontWeight: 600 }}>{COMPANY} is a software tool and does not provide medical advice. All clinical decisions are the sole responsibility of the treating physician.</p>
          </Section>

          <Section title="5. Patient Data — Important">
            <p>In the context of this Service:</p>
            <ul>
              <li><strong>You (the Clinic) are the Data Fiduciary</strong> for your patients' personal and health data, as defined under the Digital Personal Data Protection Act, 2023 (DPDPA).</li>
              <li><strong>{COMPANY} acts as a Data Processor</strong> and processes patient data only on your instructions and for the purpose of providing the Service.</li>
              <li>You are responsible for ensuring you have a valid legal basis (typically patient consent) for processing patient data in this platform.</li>
              <li>You must inform patients about their rights under applicable law, including the right to access, correct, or erase their data.</li>
            </ul>
            <p>Our obligations as a Data Processor are described further in our <Link to="/privacy" style={{ color: "hsl(var(--primary))" }}>Privacy Policy</Link>.</p>
          </Section>

          <Section title="6. Acceptable Use">
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for any unlawful purpose or in violation of any applicable laws or regulations</li>
              <li>Share login credentials between multiple users</li>
              <li>Attempt to reverse-engineer, decompile, or extract the source code of the Service</li>
              <li>Upload or transmit any malicious code, viruses, or harmful data</li>
              <li>Use the Service to store data unrelated to your clinic's operations</li>
              <li>Access or attempt to access data belonging to other clinics</li>
              <li>Scrape, crawl, or extract data from the platform through automated means</li>
              <li>Resell or sublicense access to the Service without our written consent</li>
            </ul>
          </Section>

          <Section title="7. Subscription and Payment">
            <p>Access to the Service may be subject to subscription fees as communicated at the time of sign-up or renewal. Unless otherwise agreed:</p>
            <ul>
              <li>Subscription fees are billed in advance on a monthly or annual basis</li>
              <li>All fees are in Indian Rupees (INR) and are exclusive of applicable taxes (including GST)</li>
              <li>Fees are non-refundable except as required by law or as expressly stated in a separate written agreement</li>
              <li>We reserve the right to change pricing with 30 days' written notice</li>
              <li>Non-payment may result in suspension of your account</li>
            </ul>
          </Section>

          <Section title="8. Intellectual Property">
            <p>All intellectual property rights in the Service, including software, design, trademarks, and content, are owned by or licensed to {COMPANY}. These Terms do not grant you any rights to our intellectual property other than a limited, non-exclusive, non-transferable licence to use the Service for your clinic operations.</p>
            <p>Any data you enter into the platform (patient records, prescriptions, billing data) remains your property. You grant us a limited licence to process that data solely for the purpose of providing the Service.</p>
          </Section>

          <Section title="9. Confidentiality">
            <p>Each party agrees to keep confidential the other's non-public information and to use it only for the purposes of this agreement. This obligation survives termination of these Terms.</p>
          </Section>

          <Section title="10. Disclaimers">
            <p>The Service is provided "as is" and "as available". To the fullest extent permitted by law, we disclaim all warranties, express or implied, including:</p>
            <ul>
              <li>Warranties of merchantability, fitness for a particular purpose, or non-infringement</li>
              <li>Any warranty that the Service will be uninterrupted, error-free, or free of harmful components</li>
              <li>Any warranty regarding the accuracy or reliability of clinical information entered by users</li>
            </ul>
          </Section>

          <Section title="11. Limitation of Liability">
            <p>To the maximum extent permitted by applicable law, {COMPANY} and its directors, employees, and agents shall not be liable for:</p>
            <ul>
              <li>Any indirect, incidental, special, or consequential damages</li>
              <li>Loss of profits, data, goodwill, or business opportunities</li>
              <li>Any clinical outcomes or medical errors resulting from use or misuse of the platform</li>
            </ul>
            <p>Our total aggregate liability for any claim arising out of or related to these Terms shall not exceed the fees paid by you in the three (3) months preceding the event giving rise to the claim.</p>
          </Section>

          <Section title="12. Indemnification">
            <p>You agree to indemnify and hold harmless {COMPANY} and its officers, employees, and agents from any claims, losses, damages, or expenses (including legal fees) arising from: (a) your use of the Service; (b) your breach of these Terms; (c) your violation of applicable laws; or (d) any third-party claim related to data you entered into the platform.</p>
          </Section>

          <Section title="13. Termination">
            <p>Either party may terminate this agreement with 30 days' written notice. We may suspend or terminate your account immediately if you materially breach these Terms.</p>
            <p>On termination, you may request an export of your data within 30 days. After this period, we may delete your data in accordance with our data retention policy.</p>
          </Section>

          <Section title="14. Modifications">
            <p>We may update these Terms from time to time. When we do, we will update the "Last updated" date and notify you by email or in-app notification. Continued use of the Service after the effective date of changes constitutes acceptance of the revised Terms.</p>
          </Section>

          <Section title="15. Governing Law and Dispute Resolution">
            <p>These Terms are governed by the laws of India. Any dispute arising from or related to these Terms shall first be attempted to be resolved through good-faith negotiation. If unresolved within 30 days, disputes shall be submitted to arbitration under the Arbitration and Conciliation Act, 1996, with proceedings conducted in English at a mutually agreed location in India.</p>
            <p>The courts at Pune, Maharashtra shall have jurisdiction for matters not subject to arbitration.</p>
          </Section>

          <Section title="16. Grievance Officer">
            <p>In accordance with the Information Technology Act, 2000 and the rules made thereunder, and the DPDPA 2023, the following person has been designated as the Grievance Officer:</p>
            <div style={{ background: "hsl(var(--muted))", borderRadius: "8px", padding: "1rem 1.25rem", marginTop: "0.75rem", fontSize: "0.875rem" }}>
              <p style={{ margin: "0 0 0.25rem" }}><strong>Name:</strong> Komal Dhandar</p>
              <p style={{ margin: "0 0 0.25rem" }}><strong>Designation:</strong> Chief Executive Officer</p>
              <p style={{ margin: "0 0 0.25rem" }}><strong>Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "hsl(var(--primary))" }}>{CONTACT_EMAIL}</a></p>
              <p style={{ margin: 0 }}><strong>Response time:</strong> Within 30 days of receipt of complaint</p>
            </div>
          </Section>

          <Section title="17. Contact">
            <p>For questions about these Terms:</p>
            <ul>
              <li>Email: <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "hsl(var(--primary))" }}>{CONTACT_EMAIL}</a></li>
              <li>Website: <a href={WEBSITE} target="_blank" rel="noopener noreferrer" style={{ color: "hsl(var(--primary))" }}>{WEBSITE}</a></li>
            </ul>
          </Section>

        </div>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: "2.25rem" }}>
      <h2 style={{ fontSize: "1rem", fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: "0.875rem", paddingBottom: "0.5rem", borderBottom: "1px solid hsl(var(--border))" }}>
        {title}
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        {children}
      </div>
    </section>
  );
}
